import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { MainContentBox, DataGridListScrollBox, PageTitleToolbarGrid, MainDataGrid } from '../../util/SharedStyles';
import { GridColumns, GridRowModel, GridOverlay, GridSortModel, GridSortItem, GridRowParams, GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { CircularProgress } from '@mui/material';
import { defaultGridListPageSize, Viewer } from '../../util/Constants';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';
import { AuditRecordOrderByInput, OrderDirection, EntityState, GetAuditRecordsInput } from '../../gql-types.generated';

import PageTitleBar from '../../components/PageTitleBar';
import ErrorMessage from '../../components/ErrorMessage';
import NoResultsMessage from '../../components/NoResultsMessage';
import { 
    clearState, 
    selectError, 
    selectRequestsInFlight, 
    selectAuditRecordList,
    selectAuditRecordsPagingResult
} from './AuditRecordsSlice';
import { fetchAuditRecordList } from './AuditRecordsActions';
import AuditRecordsFilterBar from '../../components/filters/AuditRecordsFilterBar';
import FiltersButton from '../../components/buttons/FiltersButton';
import GridCellDualVert from '../../components/listItems/GridCellDualVert';
import AuditRecordDetailsDialog from '../../components/dialogs/AuditRecordDetailsDialog';
import { capitalizeWithUnderscoreRemoval, useTitle } from '../../util/Common';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import GridListFooter from '../../components/paging/GridListFooter';

interface AuditRecordsProps {
    viewer: Viewer | undefined;
}

const AuditRecords: React.FC<AuditRecordsProps> = (props) => {
    const { viewer } = props;
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [contentAreaHeight, setContentAreaHeight] = useState('200px');
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterEntityName, setFilterEntityName] = useState<string | undefined>(undefined);
    const [filterEntityState, setFilterEntityState] = useState<EntityState | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [auditRecordRows, setAuditRecordRows] = useState<GridRowModel[] | undefined>(undefined);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRowId, setDetailsRowId] = useState<string | undefined>(undefined);
    const [debounceOn, setDebounceOn] = useState(false);
    const [sortFirstLoad, setSortFirstLoad] = useState(true);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'createdTime',
            sort: 'desc',
        },
    ]);
    const [serverSortModel, setServerSortModel] = useState<AuditRecordOrderByInput[] | undefined>([{
        createdTime: OrderDirection.Desc
    } as AuditRecordOrderByInput]);

    const [pageSize, setPageSize] = useState(defaultGridListPageSize);
    const [totalCount, setTotalCount] = useState(0);
    const auditRecords = useAppSelector(selectAuditRecordList);
    const error = useAppSelector(selectError);
    const requestsInFlight = useAppSelector(selectRequestsInFlight);
    const auditRecordsPagingResult = useAppSelector(selectAuditRecordsPagingResult);

    useTitle("Change Logs");

    const debounceOnSortingChanged = useRef(
        debounce((servSortModel) => {
            onSortModelChanged(servSortModel);
        }, 1000)
    ).current;

    useEffect(() => {
        setTimeout(() => {
            setDebounceOn(true);
        }, 200);
    }, []);

    useEffect(() => {
        setAuditRecordRows(getAuditRecordRows());

        if (!auditRecords && requestsInFlight > 0) {
            setIsLoading(true);
        } else if (requestsInFlight > 0) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [auditRecords, requestsInFlight]);

    useEffect(() => {
        const titleHeight = document.getElementById('audit-records-title-comp')?.clientHeight || 0;
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('audit-records-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = titleHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0) {
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
        }
    }, [auditRecords, filtersOpen]);

    useEffect(() => {
        if (auditRecords) {
            let numFilters = 0;
            if (filterEntityName && filterEntityName.length > 0) numFilters += 1;
            if (filterEntityState && filterEntityState.length > 0) numFilters += 1;
            if (filterDateFrom) numFilters += 1;
            if (filterDateTo) numFilters += 1;
            setFilterCount(numFilters);
        }
    }, [auditRecords, filterEntityName, filterEntityState, filterDateFrom, filterDateTo]);

    useEffect(() => {
        return () => {
            debounceOnSortingChanged.cancel();
        };
    }, [debounceOnSortingChanged]);

    const getSortRefreshInput = () => {
        return {
            after: undefined,
            limit: pageSize,
            entityName: filterEntityName,
            entityState: filterEntityState,
            order: serverSortModel,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo
        };
    };

    useEffect(() => {
        if (!sortFirstLoad) {
            const sortRefreshData = getSortRefreshInput();
            if (debounceOn !== true) {
                onSortModelChanged(sortRefreshData);
            } else {
                debounceOnSortingChanged(sortRefreshData);
            }
        } else {
            setSortFirstLoad(false);
        }
    }, [serverSortModel]);

    useEffect(() => {
        if (auditRecordsPagingResult?.totalCount) {
            setTotalCount(auditRecordsPagingResult.totalCount);
        }
    }, [auditRecordsPagingResult?.totalCount]);

    useEffect(() => {
        if (totalCount > 0) {
            // Refresh list for new page size
            loadPage(undefined);
        }
    }, [pageSize]);

    const getAuditRecordRows = () => {
        if (auditRecords && auditRecords.length > 0) {
            return auditRecords.map((record) => ({
                _raw: record,
                id: record.id,
                entityName: record.entityName,
                entityId: record.entityId,
                entityState: record.entityState,
                userName: record.userName,
                createdTime: record.createdTime,
                oldValues: record.oldValues,
                newValues: record.newValues
            }));
        }
        return [];
    };

    const auditRecordColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'ENTITY NAME',
                field: 'entityName',
                flex: 2,
                minWidth: 150,
                cellClassName: "ediDataGridCellFirstChild",
                sortable: true
            },
            {
                headerName: 'OLD VALUES',
                field: 'oldValues',
                flex: 3,
                minWidth: 200,
                cellClassName: "ediDataGridCellOverflowHidden",
                sortable: false
            },
            {
                headerName: 'NEW VALUES',
                field: 'newValues',
                flex: 3,
                minWidth: 200,
                cellClassName: "ediDataGridCellOverflowHidden",
                sortable: false
            },
            {
                headerName: 'ENTITY STATE',
                field: 'entityState',
                flex: 1,
                minWidth: 120,
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    const displayValue = capitalizeWithUnderscoreRemoval(value, " ");
                    return (displayValue);
                },
                sortable: false //TODO: Change to true once backend is fixed to handle enum sorting
            },
            {
                headerName: 'CHANGE DATE',
                field: 'createdTime',
                flex: 1,
                minWidth: 150,
                renderCell: (params: GridRenderCellParams) => {
                    const dateValue = params.value ? getShortDateString(params.value) : undefined;
                    const timeValue = params.value ? getTimeString(params.value, { includeSeconds: true }) : undefined;
                    return <GridCellDualVert header={dateValue} sub={timeValue} />;
                },
                sortable: true
            },
            {
                headerName: 'CHANGED BY',
                field: 'userName',
                flex: 1,
                minWidth: 120,
                sortable: true
            },
        ],
        [],
    );

    const GetApiRef = () => {
        return useGridApiContext();
    }

    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Audit Records' error={error}></ErrorMessage>
                )}
                {!error && !isLoading && (auditRecords && auditRecords.length > 0 && requestsInFlight > 0) && (
                    <CircularProgress aria-label={'progress spinner'} key={'audit-records-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
                {!error && (!auditRecords) && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Audit Records' error={error}></ErrorMessage>
                )}
                {!error && (auditRecords && auditRecords.length <= 0 && filterCount > 0) && (
                    <NoResultsMessage topMargin={6} message="Try different filters to see results." />
                )}
            </GridOverlay>
        );
    };

    const getServerSortEntryFromGridSortEntry = (entry: GridSortItem) => {
        let newModel = {} as AuditRecordOrderByInput;
        switch (entry.field) {
            case 'createdTime':
                newModel.createdTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'entityName':
                newModel.entityName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'entityState':
                newModel.entityState = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'userName':
                newModel.userName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
        }
        return newModel;
    };

    const createdAndSetServerSortModel = (model: GridSortModel) => {
        if (model && model.length > 0) {
            let newArray = [] as AuditRecordOrderByInput[];
            let i = 0;
            for (i; i < model.length; ++i) {
                newArray.push(getServerSortEntryFromGridSortEntry(model[i]));
            }
            setServerSortModel(newArray);
        } else {
            setServerSortModel(undefined);
        }
    };
    
    const onSortModelChange = (model: GridSortModel) => {
        createdAndSetServerSortModel(model);
        setSortModel(model);
    };
    
    const onFiltersClick = () => {
        setFiltersOpen(!filtersOpen);
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        setFiltersCleared(true);
        setFilterCount(0);
        setFiltersOpen(true);
    };

    const onSortModelChanged = (refreshInput: GetAuditRecordsInput) => {
        dispatch(clearState());
        dispatch(fetchAuditRecordList(refreshInput));
    };

    const loadPage = (endEdge: string | undefined) => {
        dispatch(fetchAuditRecordList({
            after: endEdge,
            limit: pageSize,
            entityName: filterEntityName,
            entityState: filterEntityState,
            order: serverSortModel,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo
        }));
    };

    const handlePageLoad = () => {
        if (!auditRecordsPagingResult) {
            return;
        }
        if (!auditRecordsPagingResult.cursor?.nextPage) {
            return;
        }

        loadPage(auditRecordsPagingResult.cursor.nextPage);
    };
    
    const refreshFilters = (
        filterEntity: string | undefined,
        filterState: EntityState | undefined,
        filterCreatedDateFrom?: DateTime | undefined,
        filterCreatedDateTo?: DateTime | undefined
    ) => {
        setFilterEntityName(filterEntity);
        setFilterEntityState(filterState);
        setFilterDateFrom(filterCreatedDateFrom ?? DateTime.now().minus({ days: 7 }));
        setFilterDateTo(filterCreatedDateTo ?? DateTime.now());

        dispatch(clearState());
        dispatch(fetchAuditRecordList({
            after: undefined,
            limit: pageSize,
            entityName: filterEntity,
            entityState: filterState,
            order: serverSortModel,
            dateFrom: filterCreatedDateFrom,
            dateTo: filterCreatedDateTo
        }));
    };

    const onRowClickHandler = (params: GridRowParams) => {
        if (params && params.row._raw) {
            setDetailsRowId(params.row.id as string);
            setDetailsOpen(true);
        }
    };

    const onDetailsDialogClose = () => {
        setDetailsRowId(undefined);
        setDetailsOpen(false);
    };

    const customFooter = () => {
        return (
            <GridListFooter
                pageSize={pageSize}
                onPageSizeChange={(newPageSize: number) => {
                    setPageSize(newPageSize);
                }}
            />
        );
    };

    return (
        <MainContentBox>
            <PageTitleBar text='Change Logs' id="audit-records-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="filters"
                    />
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <AuditRecordsFilterBar
                id="audit-records-filter-bar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGrid
                    loading={isLoading}
                    rowHeight={52}
                    aria-label="Audit Records List"
                    rowCount={totalCount}
                    hideFooterSelectedRowCount
                    disableColumnMenu
                    disableColumnFilter
                    rows={auditRecordRows ?? []}
                    columns={auditRecordColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    sortingMode="server"
                    onSortModelChange={onSortModelChange}
                    onRowsScrollEnd={handlePageLoad}
                    disableSelectionOnClick
                    onRowClick={onRowClickHandler}
                    components={{
                        LoadingOverlay: loadingOverlay,
                        NoRowsOverlay: noRowsOverlay,
                        Footer: customFooter
                    }}
                />
            </DataGridListScrollBox>
            <AuditRecordDetailsDialog
                isOpen={detailsOpen}
                auditRecord={auditRecords?.find(x => x.id === detailsRowId)}
                onClose={onDetailsDialogClose}
                error={error}
            />
        </MainContentBox>
    );
}

export default AuditRecords;
